import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

import {
  fetchMeterValues,
  fetchTransaction,
  type MeterValueSample,
  type PhaseSnapshot,
  type TransactionDetail,
} from '@/api/transactions-client';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer } from '@/components/ui/chart';
import { TxOcppFramesPanel } from '@/components/TxOcppFramesPanel';
import { useInvalidateOnCpEvents } from '@/hooks/use-invalidate-on-cp-events';
import { useSubscription } from '@/hooks/use-subscription';
import { formatUptime } from '@/lib/time';
import { useConsoleClient } from '@/lib/ws-context';

// OCPP-1.6 measurand wire forms. Power.Active.Import is in W (or kW
// when the charger reports kilowatt directly); the cumulative energy
// register is Energy.Active.Import.Register in Wh / kWh.
const POWER_MEASURAND = 'Power.Active.Import';
const ENERGY_MEASURAND = 'Energy.Active.Import.Register';

// Polling cadence for an open transaction. 5 s matches SystemPage's
// REST poll for sys/status — slow enough to be cheap, fast enough that
// an operator watching a session sees movement.
const OPEN_TX_REFETCH_MS = 5_000;

// Cap meter-values per call. The gateway accepts up to 10 000; we
// don't expect anywhere near that for a single session, but a bounded
// limit keeps the chart's data shape predictable.
const METER_VALUES_LIMIT = 5_000;

export function TransactionDetailPage() {
  const { txId: txIdParam } = useParams({ strict: false }) as { txId: string };
  const { token } = useConsoleClient();
  const txId = Number(txIdParam);

  const txQuery = useQuery({
    queryKey: ['transaction', txId],
    queryFn: () => fetchTransaction(token!, txId),
    enabled: !!token && Number.isInteger(txId) && txId > 0,
    refetchInterval: (q) => (q.state.data?.open ? OPEN_TX_REFETCH_MS : false),
  });

  const tx = txQuery.data;
  // Window for the curve queries. For a CLOSED tx the window is fixed
  // (`from`…`stopped_at`); for an OPEN tx we want the upper bound to
  // slide forward on each refetch.
  //
  // Important: do NOT put a `new Date().toISOString()` upper bound in
  // the queryKey for live transactions. Every render of this page
  // would produce a different key, TanStack Query would treat each
  // render as a brand-new query, and the chart would sit at
  // `isLoading=true` forever — never reaching the resolved data of
  // the previous render. Instead, use the literal string 'live' in
  // the key while the tx is open, and compute the real `to`
  // timestamp inside queryFn at fetch time. The polling cadence is
  // unchanged (`refetchInterval`); each refetch sends a fresh `now`.
  const from = tx?.started_at;
  const stoppedAt = tx?.stopped_at ?? null;

  const enabled = !!token && !!tx && !!from;

  const powerQuery = useQuery({
    queryKey: ['meter-values', tx?.cp_id, txId, POWER_MEASURAND, from, stoppedAt ?? 'live'],
    queryFn: () =>
      fetchMeterValues(token!, tx!.cp_id, {
        from: from!,
        to: stoppedAt ?? new Date().toISOString(),
        measurand: POWER_MEASURAND,
        connector_id: tx!.connector_id,
        limit: METER_VALUES_LIMIT,
      }),
    enabled,
    refetchInterval: tx?.open ? OPEN_TX_REFETCH_MS : false,
  });

  const energyQuery = useQuery({
    queryKey: ['meter-values', tx?.cp_id, txId, ENERGY_MEASURAND, from, stoppedAt ?? 'live'],
    queryFn: () =>
      fetchMeterValues(token!, tx!.cp_id, {
        from: from!,
        to: stoppedAt ?? new Date().toISOString(),
        measurand: ENERGY_MEASURAND,
        connector_id: tx!.connector_id,
        limit: METER_VALUES_LIMIT,
      }),
    enabled,
    refetchInterval: tx?.open ? OPEN_TX_REFETCH_MS : false,
  });

  // Live-tail samples merged on top of the REST snapshot. ClickHouse
  // (which the REST endpoint hits) has ingest lag — the chart would
  // sit at "No samples in this window" for the first 10-30 s of a
  // live session even though MeterValues are arriving over Kafka.
  // Subscribe to `meter-history` for this charger and accumulate the
  // samples that belong to THIS transaction; the chart sees both
  // REST + live as one set.
  const liveSamples = useLiveMeterSamples({
    cpId: tx?.cp_id,
    transactionId: tx?.transaction_id,
    enabled: !!tx?.open,
  });
  const livePower = useMemo(
    () => liveSamples.filter((s) => s.measurand === POWER_MEASURAND),
    [liveSamples],
  );
  const liveEnergy = useMemo(
    () => liveSamples.filter((s) => s.measurand === ENERGY_MEASURAND),
    [liveSamples],
  );
  // Per-phase snapshot derived from the same live buffer. Overlays the
  // backend's `tx.telemetry.phases` (populated from ClickHouse and
  // therefore stale/empty for the first tens of seconds of an active
  // session) with the freshest V / A / W / pf per phase we've observed
  // on the Kafka tail.
  const livePhases = useMemo(() => derivePhaseSnapshots(liveSamples), [liveSamples]);
  const mergedPhases = useMemo(
    () => mergePhases(tx?.telemetry?.phases ?? null, livePhases),
    [tx?.telemetry?.phases, livePhases],
  );

  // Live-refresh the two curves the moment a MeterValues arrives.
  // Polling stays as a safety net (best-effort Kafka tail), so the
  // 5 s cadence remains the floor; this just makes the common case
  // feel live during an active session. Refetch is no-op once the
  // tx is closed (the 'live' marker in the queryKey above flips to
  // the fixed `stopped_at` timestamp).
  const meterQueryKeys = useMemo(
    () =>
      tx?.cp_id
        ? [
            ['meter-values', tx.cp_id, txId, POWER_MEASURAND],
            ['meter-values', tx.cp_id, txId, ENERGY_MEASURAND],
            ['transaction', txId],
          ]
        : [],
    [tx?.cp_id, txId],
  );
  useInvalidateOnCpEvents({
    cpId: tx?.cp_id ?? '',
    queryKeys: meterQueryKeys,
    kinds: ['meter', 'tx-stopped'],
  });

  if (!Number.isInteger(txId) || txId <= 0) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Invalid transaction id</AlertTitle>
        <AlertDescription>tx_id must be a positive integer.</AlertDescription>
      </Alert>
    );
  }

  if (txQuery.isLoading || (!txQuery.data && !txQuery.error)) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading transaction…
      </div>
    );
  }

  if (txQuery.error || !tx) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Couldn't load transaction {txId}</AlertTitle>
        <AlertDescription>
          {txQuery.error instanceof Error ? txQuery.error.message : 'unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Header tx={tx} />
      <PowerChart
        samples={mergeSamples(powerQuery.data?.meter_values ?? [], livePower)}
        loading={powerQuery.isLoading && livePower.length === 0}
      />
      <EnergyChart
        samples={mergeSamples(energyQuery.data?.meter_values ?? [], liveEnergy)}
        loading={energyQuery.isLoading && liveEnergy.length === 0}
      />
      <PhasesCard phases={mergedPhases} />
      {tx.telemetry?.soc.last != null ? <SocCard soc={tx.telemetry.soc} /> : null}
      <TxOcppFramesPanel txId={tx.transaction_id} cpId={tx.cp_id} isOpen={tx.open} />
    </div>
  );
}

function Header({ tx }: { tx: TransactionDetail }) {
  const kwh = computeKwh(tx);
  const status = tx.open ? 'open' : 'closed';
  return (
    <div className="space-y-2">
      <div>
        <h2 className="font-mono text-lg font-semibold sm:text-xl">tx {tx.transaction_id}</h2>
        <p className="text-sm text-muted-foreground">
          on{' '}
          <Link
            to="/inspect/charge-points/$cpId"
            params={{ cpId: tx.cp_id } as never}
            className="font-mono text-foreground/80 underline-offset-2 hover:underline"
          >
            {tx.cp_id}
          </Link>{' '}
          · connector {tx.connector_id} · id_tag <span className="font-mono">{tx.id_tag}</span>
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={tx.open ? 'success' : 'muted'}>{status}</Badge>
        <Badge variant="secondary" className="font-mono text-xs" title={tx.started_at}>
          started: {formatLocalTime(tx.started_at)}
        </Badge>
        <Badge variant="secondary" className="font-mono text-xs" title={tx.stopped_at ?? ''}>
          stopped: {tx.stopped_at ? formatLocalTime(tx.stopped_at) : '—'}
        </Badge>
        <Badge variant="secondary" className="font-mono text-xs">
          duration: {formatDuration(tx)}
        </Badge>
        <Badge variant="secondary" className="font-mono text-xs">
          kWh: {kwh != null ? kwh.toFixed(3) : '—'}
        </Badge>
        {tx.stop_reason ? (
          <Badge variant="secondary" className="font-mono text-xs">
            reason: {tx.stop_reason}
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
  empty: boolean;
  loading: boolean;
}

function ChartCard({ title, children, empty, loading }: ChartCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : empty ? (
          <p className="text-sm text-muted-foreground">No samples in this window.</p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

// Active power per phase + an aggregate sum line. Per-phase samples
// (phase=L1/L2/L3) become individual lines; aggregate samples (phase
// is null — the charger reports total power, no per-phase breakdown)
// become the 'sum' line. We don't synthesize a sum from the per-phase
// samples — chargers' clocks aren't aligned tightly enough for that to
// be meaningful at sub-second resolution.
function PowerChart({ samples, loading }: { samples: MeterValueSample[]; loading: boolean }) {
  const { data, phases, hasSum } = useMemo(() => buildPowerSeries(samples), [samples]);
  const empty = data.length === 0;
  return (
    <ChartCard title="Active power" empty={empty} loading={loading}>
      <ChartContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="t" tickFormatter={formatTick} minTickGap={48} />
          <YAxis
            tickFormatter={(v: number) => v.toFixed(1)}
            label={{ value: 'kW', position: 'insideLeft', angle: -90, dy: 10, fontSize: 11 }}
          />
          <Tooltip
            labelFormatter={(t: number) => new Date(t).toLocaleString()}
            formatter={(v: number) => v.toFixed(2)}
          />
          <Legend />
          {phases.map((phase, idx) => (
            <Line
              key={phase}
              type="monotone"
              dataKey={phase}
              name={phase}
              stroke={LINE_COLOURS[idx % LINE_COLOURS.length]!}
              strokeWidth={1.5}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          ))}
          {hasSum ? (
            <Line
              type="monotone"
              dataKey="sum"
              name="sum"
              stroke="#F04E1F"
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          ) : null}
        </LineChart>
      </ChartContainer>
    </ChartCard>
  );
}

// Cumulative energy register over time. One series — Energy.Active.
// Import.Register is reported by the charger as a single (typically
// Wh) value per sample. We render it as a step-monotone line because
// it's monotone non-decreasing.
function EnergyChart({ samples, loading }: { samples: MeterValueSample[]; loading: boolean }) {
  const data = useMemo(() => buildEnergySeries(samples), [samples]);
  const empty = data.length === 0;
  return (
    <ChartCard title="Cumulative energy" empty={empty} loading={loading}>
      <ChartContainer>
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="t" tickFormatter={formatTick} minTickGap={48} />
          <YAxis
            tickFormatter={(v: number) => v.toFixed(2)}
            label={{ value: 'kWh', position: 'insideLeft', angle: -90, dy: 14, fontSize: 11 }}
          />
          <Tooltip
            labelFormatter={(t: number) => new Date(t).toLocaleString()}
            formatter={(v: number) => v.toFixed(3)}
          />
          <Line
            type="monotone"
            dataKey="kwh"
            name="kWh"
            stroke="#F04E1F"
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </LineChart>
      </ChartContainer>
    </ChartCard>
  );
}

function PhasesCard({ phases }: { phases: Record<string, PhaseSnapshot> | null }) {
  const entries = phases ? Object.entries(phases) : [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>Phases (latest sample)</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No phase telemetry available.</p>
        ) : (
          <ul className="divide-y rounded-md border" data-testid="phases-list">
            {entries.map(([phase, p]) => (
              <li
                key={phase}
                className="grid grid-cols-2 gap-1.5 px-4 py-3 text-xs sm:grid-cols-5"
                data-testid={`phase-row-${phase}`}
              >
                <div className="font-mono font-semibold text-foreground/90">{phase}</div>
                <Field k="V" v={fmtNum(p.voltage_v, 1)} />
                <Field k="A" v={fmtNum(p.current_a, 2)} />
                <Field k="W" v={fmtNum(p.power_w, 0)} />
                <Field k="pf" v={fmtNum(p.power_factor, 2)} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SocCard({
  soc,
}: {
  soc: { start: number | null; last: number | null; delta: number | null };
}) {
  return (
    <Card data-testid="soc-card">
      <CardHeader className="pb-3">
        <CardTitle>State of Charge</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-3 gap-3 text-sm">
          <SocStat k="start" v={soc.start} />
          <SocStat k="last" v={soc.last} />
          <SocStat k="delta" v={soc.delta} signed />
        </dl>
      </CardContent>
    </Card>
  );
}

function SocStat({ k, v, signed }: { k: string; v: number | null; signed?: boolean }) {
  return (
    <div className="rounded-md border p-3">
      <dt className="text-xs text-muted-foreground">{k}</dt>
      <dd className="font-mono text-base">
        {v == null ? '—' : `${signed && v > 0 ? '+' : ''}${v.toFixed(1)}%`}
      </dd>
    </div>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 sm:flex-col sm:items-start sm:gap-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono text-foreground/80">{v}</span>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------

// Per-phase line palette. Primary orange (#F04E1F) is reserved for
// the aggregate `sum` line so it reads as the headline measurement;
// phases get cool secondaries to keep the contrast.
const LINE_COLOURS = ['#3B82F6', '#22C55E', '#A855F7', '#F59E0B'];

interface PowerPoint {
  t: number;
  // Dynamically keyed by phase + 'sum'. Kept loose so Recharts can
  // pick the dataKeys.
  [k: string]: number | undefined;
}

function buildPowerSeries(samples: MeterValueSample[]): {
  data: PowerPoint[];
  phases: string[];
  hasSum: boolean;
} {
  // Group by occurred_at; one row per timestamp with a column per phase
  // (and a 'sum' column for aggregate samples). All values are
  // normalised to kW regardless of the sample's reported unit.
  const byTime = new Map<number, PowerPoint>();
  const phaseSet = new Set<string>();
  let hasSum = false;
  for (const s of samples) {
    const t = new Date(s.occurred_at).getTime();
    if (Number.isNaN(t)) continue;
    const kw = toKilowatts(s.value, s.unit);
    if (kw == null) continue;
    let row = byTime.get(t);
    if (!row) {
      row = { t };
      byTime.set(t, row);
    }
    if (s.phase) {
      phaseSet.add(s.phase);
      row[s.phase] = kw;
    } else {
      hasSum = true;
      row.sum = kw;
    }
  }
  const data = Array.from(byTime.values()).sort((a, b) => a.t - b.t);
  // Stable phase order (L1 < L2 < L3 < anything else).
  const phases = Array.from(phaseSet).sort((a, b) => a.localeCompare(b));
  return { data, phases, hasSum };
}

interface EnergyPoint {
  t: number;
  kwh: number;
}

function buildEnergySeries(samples: MeterValueSample[]): EnergyPoint[] {
  const out: EnergyPoint[] = [];
  for (const s of samples) {
    const t = new Date(s.occurred_at).getTime();
    if (Number.isNaN(t)) continue;
    const kwh = toKilowattHours(s.value, s.unit);
    if (kwh == null) continue;
    out.push({ t, kwh });
  }
  return out.sort((a, b) => a.t - b.t);
}

// Fold per-phase live samples down to a single latest-value snapshot
// per phase, shaped to match the backend's `PhaseSnapshot`. Only
// samples with a phase label contribute — aggregate samples (phase
// null) feed the Active-power chart's `sum` line, not this card.
//
// The `samples` buffer is append-only, so iterating linearly and
// overwriting yields the newest value per (phase, measurand). Power is
// normalised to W to match the backend field name (`power_w`); voltage
// and current are pass-through since their units are already V / A.
function derivePhaseSnapshots(samples: MeterValueSample[]): Record<string, PhaseSnapshot> {
  const out: Record<string, PhaseSnapshot> = {};
  for (const s of samples) {
    if (!s.phase) continue;
    const p = out[s.phase] ?? {
      voltage_v: null,
      current_a: null,
      power_w: null,
      power_factor: null,
      occurred_at: null,
    };
    switch (s.measurand) {
      case 'Voltage':
        p.voltage_v = s.value;
        break;
      case 'Current.Import':
        p.current_a = s.value;
        break;
      case 'Power.Active.Import':
        p.power_w = s.unit === 'kW' ? s.value * 1000 : s.value;
        break;
      case 'Power.Factor':
        p.power_factor = s.value;
        break;
      default:
        continue;
    }
    if (!p.occurred_at || s.occurred_at > p.occurred_at) {
      p.occurred_at = s.occurred_at;
    }
    out[s.phase] = p;
  }
  return out;
}

// Merge the backend's phase snapshot with the live-derived one, field
// by field. Live wins where it has data; backend fills the gaps (and
// carries the final snapshot after the tx closes and live goes
// silent). Returns null when both sides are empty so PhasesCard falls
// through to its "no phase telemetry" placeholder.
function mergePhases(
  backend: Record<string, PhaseSnapshot> | null,
  live: Record<string, PhaseSnapshot>,
): Record<string, PhaseSnapshot> | null {
  const phases = new Set<string>([...Object.keys(backend ?? {}), ...Object.keys(live)]);
  if (phases.size === 0) return null;
  const out: Record<string, PhaseSnapshot> = {};
  for (const phase of phases) {
    const b = backend?.[phase];
    const l = live[phase];
    out[phase] = {
      voltage_v: l?.voltage_v ?? b?.voltage_v ?? null,
      current_a: l?.current_a ?? b?.current_a ?? null,
      power_w: l?.power_w ?? b?.power_w ?? null,
      power_factor: l?.power_factor ?? b?.power_factor ?? null,
      occurred_at: l?.occurred_at ?? b?.occurred_at ?? null,
    };
  }
  return out;
}

function toKilowatts(v: number, unit: string): number | null {
  switch (unit) {
    case 'W':
      return v / 1000;
    case 'kW':
      return v;
    default:
      return null;
  }
}

function toKilowattHours(v: number, unit: string): number | null {
  switch (unit) {
    case 'Wh':
      return v / 1000;
    case 'kWh':
      return v;
    default:
      return null;
  }
}

function computeKwh(tx: TransactionDetail): number | null {
  if (tx.meter_stop_wh == null) return null;
  return (tx.meter_stop_wh - tx.meter_start_wh) / 1000;
}

function formatDuration(tx: TransactionDetail): string {
  if (tx.open) {
    return formatUptime(tx.started_at);
  }
  if (!tx.stopped_at) return '—';
  const startMs = new Date(tx.started_at).getTime();
  const stopMs = new Date(tx.stopped_at).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(stopMs) || stopMs < startMs) return '—';
  const sec = Math.floor((stopMs - startMs) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86_400) {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(sec / 86_400);
  const h = Math.floor((sec % 86_400) / 3600);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

function formatLocalTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function formatTick(t: number): string {
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtNum(v: number | null, digits: number): string {
  if (v == null) return '—';
  return v.toFixed(digits);
}

// ---- live meter-history tail ---------------------------------------------

// Convert protobuf-enum-style measurand names (`POWER_ACTIVE_IMPORT`,
// `ENERGY_ACTIVE_IMPORT_REGISTER`) — what the meter-history WS
// channel emits — into OCPP-1.6 wire form (`Power.Active.Import`,
// `Energy.Active.Import.Register`) so REST + live samples merge
// against the same measurand key.
const MEASURAND_FROM_ENUM: Record<string, string> = {
  POWER_ACTIVE_IMPORT: 'Power.Active.Import',
  POWER_FACTOR: 'Power.Factor',
  ENERGY_ACTIVE_IMPORT_REGISTER: 'Energy.Active.Import.Register',
  ENERGY_ACTIVE_IMPORT_INTERVAL: 'Energy.Active.Import.Interval',
  VOLTAGE: 'Voltage',
  CURRENT_IMPORT: 'Current.Import',
  SOC: 'SoC',
  TEMPERATURE: 'Temperature',
  FREQUENCY: 'Frequency',
};

function normaliseMeasurand(m: string): string {
  return MEASURAND_FROM_ENUM[m] ?? m;
}

function useLiveMeterSamples({
  cpId,
  transactionId,
  enabled,
}: {
  cpId: string | undefined;
  transactionId: number | undefined;
  enabled: boolean;
}): MeterValueSample[] {
  // useSubscription requires a non-null params record; pass an empty
  // cp_id when we shouldn't subscribe and gate the accumulation
  // below on `enabled` so the closed-tx render never appends.
  const sub = useSubscription('meter-history', { cp_id: enabled && cpId ? cpId : '' });
  const [samples, setSamples] = useState<MeterValueSample[]>([]);
  const lastSeenRef = useRef<unknown>(null);

  // Reset the buffer when the tx or charger changes — otherwise
  // navigating between transactions would carry stale samples.
  useEffect(() => {
    setSamples([]);
    lastSeenRef.current = null;
  }, [cpId, transactionId]);

  useEffect(() => {
    if (!enabled) return;
    const delta = sub.lastDelta;
    if (!delta || delta.kind !== 'meter-history') return;
    if (lastSeenRef.current === delta.append) return;
    lastSeenRef.current = delta.append;
    const s = delta.append;
    if (s.transaction_id == null || s.transaction_id !== transactionId) return;
    setSamples((prev) => [
      ...prev,
      {
        cp_id: s.cp_id,
        connector_id: s.connector_id,
        transaction_id: s.transaction_id,
        occurred_at: s.recorded_at,
        measurand: normaliseMeasurand(s.measurand),
        phase: s.phase ?? null,
        unit: s.unit ?? '',
        value: s.value,
      },
    ]);
  }, [sub.lastDelta, enabled, transactionId]);

  return samples;
}

// Merge REST + live samples, de-duping on (occurred_at, measurand,
// phase) so an overlap (REST poll picks up a sample also seen on the
// live tail) doesn't double-plot.
function mergeSamples(rest: MeterValueSample[], live: MeterValueSample[]): MeterValueSample[] {
  if (live.length === 0) return rest;
  const seen = new Set<string>();
  const out: MeterValueSample[] = [];
  for (const s of [...rest, ...live]) {
    const k = `${s.occurred_at}|${s.measurand}|${s.phase ?? ''}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}
